import type { FC } from 'react';
import React, { createContext, useContext, useState } from 'react';
import { cn } from '../../lib/utils';

type StepContextValue = {
  isCompleting: boolean;
  stepIndex: number;
  currentStep: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  nextStep: () => void;
  previousStep: () => void;
};

const StepContext = createContext<StepContextValue | null>(null);

type StepperProps = {
  children: React.ReactNode;
  onComplete?: () => void | Promise<void>;
  onStepChanged?: (currentStep: number) => void;
  currentStep?: number; // external control prop
  setCurrentStep?: (step: number) => void; // external control function
};

export const Stepper = ({ className }: { className?: string }) => {
  const { totalSteps, stepIndex } = useStep();

  return (
    <div className={cn('flex w-full items-center justify-between gap-4', className)}>
      <div className="flex-1">
        <div className="flex h-3 w-full overflow-hidden rounded-full md:h-4">
          {Array.from({ length: totalSteps }, (_, index) => {
            const isActive = stepIndex === index;
            const isEvenIndex = index % 2 === 0; // 0, 2, 4 (Steps 1, 3, 5) -> Male/Outer
            const isFirst = index === 0;
            const isLast = index === totalSteps - 1;
            // Visual Configurations
            const arrowDepth = 12;
            const gapSize = 2;
            const overlap = arrowDepth - gapSize;

            // SHAPE LOGIC:
            // 1. First Step: Always Round Left, Arrow Right.
            // 2. Middle Odd Index (Step 2, 4): Inner Left, Inner Right (Ribbon).
            // 3. Middle Even Index (Step 3, 5): Outer Left, Outer Right (Hexagon).
            // 4. Last Step: Adapts to previous. If Prev was Inner, this starts Outer.

            let clipPath = '';

            if (isFirst) {
              // Step 1: Round Left (handled by container), Arrow Right
              clipPath = `polygon(0 0, calc(100% - ${arrowDepth}px) 0, 100% 50%, calc(100% - ${arrowDepth}px) 100%, 0 100%)`;
            } else if (isLast) {
              if (isEvenIndex) {
                // If Last is Even (e.g. Step 3), it follows an Inner, so it must start Outer (Arrow Left).
                clipPath = `polygon(0 50%, ${arrowDepth}px 0, 100% 0, 100% 100%, ${arrowDepth}px 100%)`;
              } else {
                // If Last is Odd (e.g. Step 4), it follows an Outer, so it must start Inner (Notch Left).
                clipPath = `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${arrowDepth}px 50%)`;
              }
            } else {
              // Middle Steps
              if (isEvenIndex) {
                // Step 3, 5... : Outer Left, Outer Right (Hexagon)
                // Note: '0 50%' is the start arrow tip
                clipPath = `polygon(0 50%, ${arrowDepth}px 0, calc(100% - ${arrowDepth}px) 0, 100% 50%, calc(100% - ${arrowDepth}px) 100%, ${arrowDepth}px 100%)`;
              } else {
                // Step 2, 4... : Inner Left, Inner Right (Ribbon/Bowtie)
                // Note: '12px 50%' is the start notch vertex, 'calc-12 50%' is end notch vertex
                clipPath = `polygon(0 0, 100% 0, calc(100% - ${arrowDepth}px) 50%, 100% 100%, 0 100%, ${arrowDepth}px 50%)`;
              }
            }

            return (
              <div
                className={cn(
                  'relative h-full flex-1 transition-colors duration-300 ease-in-out',
                  // Current step is darker teal, others are light mint
                  isActive ? 'bg-primary' : 'bg-primary/20',
                )}
                key={index}
                style={{
                  clipPath,
                  // Pull items start to overlap.
                  marginInlineStart: isFirst ? 0 : `-${overlap}px`,
                  // Even indices (Outer/Arrows) must sit ON TOP of Odd indices (Inner/Notches)
                  // to ensure the arrow tip renders cleanly over the notch.
                  zIndex: isEvenIndex ? 10 : 1,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const StepperProvider: FC<StepperProps> = ({
  children,
  onComplete,
  onStepChanged,
  currentStep: propCurrentStep,
  setCurrentStep: propSetCurrentStep,
}) => {
  const [stateCurrentStep, stateSetCurrentStep] = useState(1);
  const [isCompleting, setIsCompleting] = useState(false);

  // Determine if props are provided, otherwise use state
  const isControlled = propCurrentStep !== undefined && propSetCurrentStep !== undefined;
  const currentStep = isControlled ? propCurrentStep : stateCurrentStep;
  const setCurrentStep = isControlled ? propSetCurrentStep : stateSetCurrentStep;

  const totalSteps = React.Children.count(children);

  const handleComplete = async () => {
    try {
      if (!onComplete) {
        return;
      }

      setIsCompleting(true);

      await onComplete();

      setIsCompleting(false);
    } catch (error) {
      setIsCompleting(false);

      throw error;
    }
  };

  const handleStepChange = (nextStep: number) => {
    setCurrentStep(nextStep);
    onStepChanged?.(nextStep);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      void handleStepChange(currentStep + 1);
    } else {
      void handleComplete();
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      void handleStepChange(currentStep - 1);
    }
  };

  // Empty stepper
  if (totalSteps === 0) {
    return null;
  }

  const currentChild = React.Children.toArray(children)[currentStep - 1];

  const stepContextValue: StepContextValue = {
    isCompleting,
    stepIndex: currentStep - 1,
    currentStep,
    totalSteps,
    isFirst: currentStep === 1,
    isLast: currentStep === totalSteps,
    nextStep,
    previousStep,
  };

  return <StepContext.Provider value={stepContextValue}>{currentChild}</StepContext.Provider>;
};

/** Hook for children to use the step context */
export const useStep = (): StepContextValue => {
  const context = useContext(StepContext);

  if (!context) {
    throw new Error('useStep must be used within a Stepper');
  }

  return context;
};
